import type { Citation } from '../../state/chatTypes';

/**
 * Maximum number of sources listed in the trailing sources sentence. The
 * sentence truncates to the first {@link MAX_SENTENCE_SOURCES} deduped
 * citations in original order; inline `[N]` markers in the message body are
 * intentionally unaffected and continue to render and linkify all sources.
 */
const MAX_SENTENCE_SOURCES = 3;

/**
 * True when `url` is a well-formed `https:` URL. Citation links require
 * HTTPS to avoid mixed-content failures and referrer leakage to insecure
 * origins (mirrors the visualization-image guard).
 * @param url - Candidate citation URL.
 */
export const isHttpsCitationUrl = (url: string): boolean => {
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * De-dupes citations by index, preserving first-seen order and dropping
 * entries with an empty `displayLabel`. The first non-empty-label entry
 * for each index wins. This is the single source of dedupe precedence so
 * the inline `[N]` marker linkifier and the "Sources" footer always
 * resolve a given index to the same citation.
 * @param citations - Assistant-message citations, if any.
 */
export const dedupeCitationsByIndex = (
  citations: ReadonlyArray<Citation> | undefined
): ReadonlyArray<Citation> => {
  const seen = new Set<number>();
  const deduped: Citation[] = [];
  for (const citation of citations ?? []) {
    if (citation.displayLabel === '' || seen.has(citation.index)) {
      continue;
    }
    seen.add(citation.index);
    deduped.push(citation);
  }
  return deduped;
};

/**
 * Builds a map of citation index → HTTPS URL for the inline `[N]` marker
 * linkifier. Citations without an HTTPS URL are omitted so their markers
 * stay as plain text. Built from {@link dedupeCitationsByIndex}, so the
 * marker and the sources sentence agree on which citation each index maps
 * to on duplicate indices.
 * @param citations - Assistant-message citations, if any.
 */
export const buildCitationUrlByIndex = (
  citations: ReadonlyArray<Citation> | undefined
): ReadonlyMap<number, string> => {
  const map = new Map<number, string>();
  for (const citation of dedupeCitationsByIndex(citations)) {
    if (citation.url !== undefined && isHttpsCitationUrl(citation.url)) {
      map.set(citation.index, citation.url);
    }
  }
  return map;
};

/**
 * One renderable piece of the sources sentence: a deduped {@link Citation}
 * plus the literal text that must precede it. Separators encode the
 * grammar of the joined list (comma series with a trailing conjunction
 * and an Oxford comma for three or more sources):
 *
 * - first source → `''`
 * - middle source → `', '`
 * - last of two → `' {conjunction} '` (e.g. `' and '`)
 * - last of three or more → `', {conjunction} '` (Oxford comma)
 */
export type CitationSentenceSegment = {
  citation: Citation;
  separatorBefore: string;
};

/**
 * Resolves the literal separator that precedes the source at `position`
 * within a list of `total` sources, given the localized `conjunction`.
 * Pure string assembly — the caller supplies the translated word.
 */
const resolveSeparatorBefore = (
  position: number,
  total: number,
  conjunction: string
): string => {
  if (position === 0) {
    return '';
  }
  const isLast = position === total - 1;
  if (!isLast) {
    return ', ';
  }
  return total > 2 ? `, ${conjunction} ` : ` ${conjunction} `;
};

/**
 * Builds the ordered segments for the "For more details, check out the …"
 * sources sentence. De-dupes via {@link dedupeCitationsByIndex} so the
 * sentence and the inline `[N]` marker linkifier resolve a given index to
 * the same citation, then caps the list to the first
 * {@link MAX_SENTENCE_SOURCES} entries (original order) and annotates each
 * with the literal separator that precedes it. The cap is scoped to this
 * sentence only — inline `[N]` markers still linkify every source. Returns
 * an empty list when there are no renderable citations so the caller can
 * omit the sentence entirely.
 * @param citations - Assistant-message citations, if any.
 * @param conjunction - Localized conjunction word (e.g. `and`) joining the
 *   final source to the rest of the list.
 */
export const buildCitationSentenceSegments = (
  citations: ReadonlyArray<Citation> | undefined,
  conjunction: string
): ReadonlyArray<CitationSentenceSegment> => {
  const visibleItems = dedupeCitationsByIndex(citations).slice(
    0,
    MAX_SENTENCE_SOURCES
  );
  // resolveSeparatorBefore adds its own spacing around the conjunction, so
  // trim once here to stay resilient to translations that carry leading or
  // trailing whitespace (which would otherwise render as doubled spaces).
  const trimmedConjunction = conjunction.trim();
  return visibleItems.map((citation, position) => ({
    citation,
    separatorBefore: resolveSeparatorBefore(
      position,
      visibleItems.length,
      trimmedConjunction
    ),
  }));
};
